import { useEffect } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/ui/use-toast";
import { OAUTH_ERROR_CODES, OAUTH_ERROR_PARAMS, OAUTH_ERROR_PARAM_KEYS } from "@/lib/social/oauth-constants";

/**
 * Hook to handle OAuth error parameters from URL and display user-friendly toast messages.
 * Automatically cleans up error parameters after displaying the message.
 */
export function useOAuthErrorHandling(): void {
  const { t } = useTranslation();
  const { toast } = useToast();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!searchParams || !pathname) return;

    const error = searchParams.get(OAUTH_ERROR_PARAM_KEYS.ERROR);
    const errorMessage = searchParams.get(OAUTH_ERROR_PARAM_KEYS.ERROR_MESSAGE);

    if (error) {
      // Show user-friendly error message
      let title = t("account.connection_failed", "Connection Failed");
      let description = errorMessage || t("account.connection_error_generic", "Failed to connect your account.");

      // Special handling for YouTube no channel error
      if (error === OAUTH_ERROR_CODES.YOUTUBE_NO_CHANNEL) {
        title = t("account.youtube_channel_required", "YouTube Channel Required");
        description = t(
          "account.youtube_no_channel_description",
          "Your Google account authenticated successfully, but you don't have a YouTube channel yet. Please create a channel at youtube.com first, then try connecting again."
        );
      }

      toast({
        variant: "destructive",
        title,
        description,
      });

      // Clean up only error-related params to preserve other state
      const params = new URLSearchParams(searchParams.toString());
      OAUTH_ERROR_PARAMS.forEach((param) => params.delete(param));

      const newUrl = params.toString()
        ? `${pathname}?${params.toString()}`
        : pathname;

      router.replace(newUrl, { scroll: false });
    }
  }, [searchParams, toast, t, router, pathname]);
}
